import type { SubscriptionState } from '../../../__generated__/types.gqlgenerated';

import { GdprTosNotice } from './GdprTosNotice';
import { Box, Text, Switch, Alert, Heading, Button, CheckCircle } from '../../../mads';
import {
    type CanonicalEnvironment,
    isProductionEnvironment,
} from '../../environment';
import { useI18n } from '../../I18n';
import { useCreateJobAlertMutation } from './graphql/__generated__/createJobAlert.gqlgenerated';
import { logElementView } from '../../native-modules/tare/logElementView';
import { logTapButtonWithExtraParams } from '../../native-modules/tare/logTapButtonWithExtraParams';
import { useTareData } from '../../native-modules/tare/TareDataContext';
import { MarkupComponents } from '../MarkupComponents/MarkupComponents';

import { borderRadius, spacing, colors } from '@indeed/ifl-tokens/react-native';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';

const SUBSCRIPTIONS_PROD_URL = 'https://subscriptions.indeed.com';
const SUBSCRIPTIONS_QA_URL = 'https://subscriptions.qa.indeed.net';

const getSubscriptionsBaseUrl = (buildEnvironment: CanonicalEnvironment | undefined): string => {
    return isProductionEnvironment(buildEnvironment ?? 'prod')
        ? SUBSCRIPTIONS_PROD_URL
        : SUBSCRIPTIONS_QA_URL;
};

const htmlToMarkupFormat = (html: string): string =>
    html.replace(/<strong>([^<]*)<\/strong>/g, '[$1|bold]');

const BoldTextRenderer = ({ children }: { children: string }): React.JSX.Element => (
    <Text level={3} sx={{ fontWeight: 'bold' }}>
        {children}
    </Text>
);

const boldTextRenderer = (children: string): React.ReactNode => (
    <BoldTextRenderer>{children}</BoldTextRenderer>
);

interface BodyTextProps {
    queryWhat?: string;
    alertLocation: string;
}

const BodyText = ({ queryWhat, alertLocation }: BodyTextProps): React.JSX.Element => {
    const i18n = useI18n();
    let bodyHtml: string;

    if (queryWhat && alertLocation) {
        bodyHtml = i18n.gettext(
            'Get email updates about <strong>{0}</strong> jobs in <strong>{1}</strong>',
            [queryWhat, alertLocation],
        );
    } else if (queryWhat) {
        bodyHtml = i18n.gettext('Get email updates about <strong>{0}</strong> jobs', [
            queryWhat,
        ]);
    } else if (alertLocation) {
        bodyHtml = i18n.gettext('Get email updates about jobs in <strong>{0}</strong>', [
            alertLocation,
        ]);
    } else {
        return <Text level={3}>{i18n.gettext('Get email updates about jobs')}</Text>;
    }

    return (
        <Text level={3}>
            <MarkupComponents components={{ bold: boldTextRenderer }}>
                {htmlToMarkupFormat(bodyHtml)}
            </MarkupComponents>
        </Text>
    );
};

interface SuccessStateProps {
    manageAlertsHref: string;
    pageName: string;
    isExistingAlert: boolean;
    navigateToUrl?: (url: string) => void;
}

const SuccessState = ({
    manageAlertsHref,
    pageName,
    isExistingAlert,
    navigateToUrl,
}: SuccessStateProps): React.JSX.Element => {
    const i18n = useI18n();
    const hasLoggedSuccessImpression = useRef(false);

    useEffect(() => {
        if (hasLoggedSuccessImpression.current) {
            return;
        }
        hasLoggedSuccessImpression.current = true;
        void logElementView(pageName, 'jobAlertSuccessCard', 'BANNER', [pageName], {
            isExistingAlert,
        });
    }, [pageName, isExistingAlert]);

    const handleEditEmailPress = (): void => {
        void logTapButtonWithExtraParams(pageName, 'jobAlertEditEmailSettings', [pageName], {});
        if (navigateToUrl) {
            navigateToUrl(manageAlertsHref);
        }
    };

    return (
        <Box sx={styles.successCard}>
            <Box sx={styles.successHeader}>
                <Heading level={3} sx={{ flex: 1 }}>
                    {i18n.gettext('Job Alert Created')}
                </Heading>
                <CheckCircle color={colors.backgroundStatusSuccess} size="md" />
            </Box>
            <Text sx={{ marginTop: spacing.s2, marginBottom: spacing.s3 }}>
                {i18n.gettext("You'll receive your first email update when jobs become available.")}
            </Text>
            <Box sx={styles.buttonContainer}>
                <Button variant="secondary" size="md" onPress={handleEditEmailPress}>
                    {i18n.gettext('Edit email settings')}
                </Button>
            </Box>
        </Box>
    );
};

interface ToggleStateProps {
    queryWhat?: string;
    alertLocation: string;
    isToggleEnabled: boolean;
    onToggleChange: (enabled: boolean) => void;
    isFailed: boolean;
    language?: string;
    country?: string;
    pageName: string;
    navigateToUrl?: (url: string) => void;
}

const ToggleState = ({
    queryWhat,
    alertLocation,
    isToggleEnabled,
    onToggleChange,
    isFailed,
    language,
    country,
    pageName,
    navigateToUrl,
}: ToggleStateProps): React.JSX.Element => {
    const i18n = useI18n();
    return (
        <Box sx={styles.toggleCard}>
            <Box sx={styles.toggleContainer}>
                <Box sx={styles.toggleTextContainer}>
                    <BodyText queryWhat={queryWhat} alertLocation={alertLocation} />
                </Box>
                <Switch
                    value={isToggleEnabled}
                    onValueChange={onToggleChange}
                    switchTestID="job-alert-toggle"
                    accessibilityHint={i18n.gettext(
                        'Turn on to receive job alerts for this search',
                    )}
                />
            </Box>
            {/* RN-2319: TOS notice hidden pending logged-out support decision */}
            {false && (
                <GdprTosNotice
                    language={language}
                    country={country}
                    pageName={pageName}
                    navigateToUrl={navigateToUrl}
                />
            )}
            {isFailed && (
                <Alert
                    status="critical"
                    sx={{ marginTop: spacing.s3 }}
                    explainerText={i18n.gettext(
                        'An error occurred creating your job alert. Please try again later.',
                    )}
                />
            )}
        </Box>
    );
};

export interface JobAlertSignupToggleProps {
    queryWhat?: string;
    queryWhere?: string;
    suggestedLocation?: string;
    searchParams?: string;
    country?: string;
    language?: string;
    pageName?: string;
    position?: string;
    existingSubscriptionState?: SubscriptionState | null;
    navigateToUrl?: (url: string) => void;
    onJobAlertCreated?: (subscriptionState: SubscriptionState) => void;
}

export function JobAlertSignupToggle(props: JobAlertSignupToggleProps): React.JSX.Element | null {
    const tareData = useTareData();
    const hasLoggedImpression = useRef(false);
    const [createJobAlert] = useCreateJobAlertMutation();

    const pageName = props.pageName ?? 'viewjob';
    const position = props.position ?? `${pageName}_rn_ja_toggle`;

    const manageAlertsHref = useMemo(() => {
        const language = props.language || 'en';
        const country = props.country || 'US';
        const buildEnvironment = tareData?.appModel?.buildEnvironment;
        const baseUrl = getSubscriptionsBaseUrl(buildEnvironment);
        const params = new URLSearchParams({
            hl: language,
            co: country,
            from: `${pageName}_rn`,
        });
        return `${baseUrl}?${params.toString()}`;
    }, [props.language, props.country, pageName, tareData?.appModel?.buildEnvironment]);

    const isPreExistingAlert =
        props.existingSubscriptionState === 'ACTIVE' ||
        props.existingSubscriptionState === 'UNCONF';
    const [isCreated, setIsCreated] = useState(isPreExistingAlert);
    const [isFailed, setIsFailed] = useState(false);
    const [isToggleEnabled, setIsToggleEnabled] = useState(false);

    // Gate on emailAddress rather than isLoggedIn: the toggle requires an email to create
    // a job alert. A logged-in SSO user without a stored email cannot use this feature.
    const userEmail = tareData?.accountStatus?.emailAddress;
    const alertLocation = props.queryWhere || props.suggestedLocation || '';

    useEffect(() => {
        if (!userEmail || isCreated || hasLoggedImpression.current) {
            return;
        }
        hasLoggedImpression.current = true;
        void logElementView(pageName, 'jobAlertToggle', 'BANNER', [pageName], {
            query: props.queryWhat || '',
            location: alertLocation,
        });
    }, [userEmail, isCreated, props.queryWhat, alertLocation, pageName]);

    if (!userEmail) {
        return null;
    }

    const submitJobAlert = async (): Promise<void> => {
        const input = {
            query: props.queryWhat || '',
            location: alertLocation,
            searchParams: props.searchParams || '',
            position,
        };

        try {
            const { data } = await createJobAlert({
                variables: { input },
            });
            const jobAlert = data?.jobAlert?.create?.jobAlert;

            if (jobAlert) {
                setIsCreated(true);
                setIsFailed(false);

                if (props.onJobAlertCreated) {
                    props.onJobAlertCreated(jobAlert.subscriptionState);
                }

                void logTapButtonWithExtraParams(pageName, 'jobAlertToggleActivate', [pageName], {
                    query: props.queryWhat || '',
                    location: alertLocation,
                    subscriptionState: jobAlert.subscriptionState,
                    jobAlertId: jobAlert.id,
                });
            } else {
                setIsFailed(true);
                setIsToggleEnabled(false);
                void logTapButtonWithExtraParams(pageName, 'jobAlertToggleError', [pageName], {
                    query: props.queryWhat || '',
                    location: alertLocation,
                });
            }
        } catch {
            setIsCreated(false);
            setIsFailed(true);
            setIsToggleEnabled(false);
            void logTapButtonWithExtraParams(pageName, 'jobAlertToggleError', [pageName], {
                query: props.queryWhat || '',
                location: alertLocation,
            });
        }
    };

    const handleToggleChange = (enabled: boolean): void => {
        setIsToggleEnabled(enabled);

        if (!enabled) {
            return;
        }

        void submitJobAlert();
    };

    return (
        <Box sx={styles.container}>
            {isCreated ? (
                <SuccessState
                    manageAlertsHref={manageAlertsHref}
                    pageName={pageName}
                    isExistingAlert={isPreExistingAlert}
                    navigateToUrl={props.navigateToUrl}
                />
            ) : (
                <ToggleState
                    queryWhat={props.queryWhat}
                    alertLocation={alertLocation}
                    isToggleEnabled={isToggleEnabled}
                    onToggleChange={handleToggleChange}
                    isFailed={isFailed}
                    language={props.language}
                    country={props.country}
                    pageName={pageName}
                    navigateToUrl={props.navigateToUrl}
                />
            )}
        </Box>
    );
}

const styles = StyleSheet.create({
    container: {
        width: '100%',
    },
    toggleCard: {
        backgroundColor: colors.backgroundPrimaryWeak,
        padding: spacing.s4,
        borderRadius: borderRadius.md,
        marginStart: spacing.s4,
        marginEnd: spacing.s4,
    },
    successCard: {
        backgroundColor: colors.backgroundStatusSuccessWeaker,
        padding: spacing.s4,
        borderRadius: borderRadius.md,
        marginStart: spacing.s4,
        marginEnd: spacing.s4,
    },
    toggleContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    toggleTextContainer: {
        flex: 1,
        marginEnd: spacing.s3,
    },
    successHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    buttonContainer: {
        alignSelf: 'flex-start',
    },
});
